import React from 'react';
import { AppShell } from '@/components';
import {
  MarketingSection,
  SectionHeader,
  GlassPanel,
} from '@/components/marketing';
import {
  pricingPlans,
  pricingAddOns,
  pricingOverages,
  pricingVolumeDiscounts,
  pricingSupportPlans,
  pricingProfessionalServices,
} from '@/content/pricing';

const PricingPage: React.FC = () => {
  return (
    <AppShell>
      <div className="bg-slate-950 text-slate-50">
        <MarketingSection theme="gradient" collapsePadding>
          <div className="grid gap-10 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:py-24">
            <div className="space-y-8">
              <SectionHeader
                overline="Pricing"
                title="Control the full lifecycle of your serverless estate—without runaway costs."
                description="Start for free on your own infrastructure, graduate to managed Relay for turnkey operations, and unlock enterprise governance when teams span clouds, regions, and revenue models."
              />
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-300">
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">Usage-based billing</span>
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">No cold-start penalties</span>
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">SOC2-ready controls</span>
              </div>
            </div>
            <GlassPanel padding="lg" className="shadow-2xl shadow-emerald-500/10">
              <h2 className="text-lg font-semibold text-white">Every plan includes</h2>
              <ul className="mt-4 space-y-3 text-sm text-slate-200">
                <li>• RUFID version graph &amp; branch-aware deployments</li>
                <li>• Execution telemetry (latency, cost, anomalies)</li>
                <li>• Policy engine with namespace isolation</li>
                <li>• Marketplace SDKs, CLI, Studio, and API access</li>
              </ul>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <SectionHeader
            overline="Plans"
            title="Choose the track that matches your program"
            description="All managed plans include Relay Studio, execution history, and namespace governance. Scale by layering commerce and compliance add-ons as needed."
            actions={
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-slate-200 transition hover:border-white hover:text-white"
              >
                Talk to sales
              </a>
            }
          />

          <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {pricingPlans.map((plan) => {
              const capacity = plan.limits && plan.limits.length > 0 ? plan.limits : undefined;
              return (
                <GlassPanel
                  key={plan.id}
                  padding="lg"
                  className={plan.highlight ? 'border-emerald-500 bg-emerald-500/10 shadow-2xl shadow-emerald-500/20' : undefined}
                >
                  <div className="space-y-3">
                    {plan.badge && (
                      <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-300">
                        {plan.badge}
                      </span>
                    )}
                    <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
                    <div className="text-3xl font-bold text-white">{plan.price}</div>
                    {plan.priceNote && <p className="text-xs uppercase tracking-wide text-slate-400">{plan.priceNote}</p>}
                    <p className="text-sm text-slate-300">{plan.tagline}</p>
                  </div>

                  {capacity && (
                    <div className="pt-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Included capacity</p>
                      <ul className="mt-3 space-y-2 text-sm text-slate-200">
                        {capacity.map((limit) => (
                          <li key={limit}>• {limit}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Key capabilities</p>
                    <ul className="mt-3 space-y-2 text-sm text-slate-200">
                      {plan.capabilities.map((capability) => (
                        <li key={capability}>• {capability}</li>
                      ))}
                    </ul>
                  </div>

                  <a
                    href={plan.cta.href}
                    className={`mt-auto inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-semibold transition ${
                      plan.highlight
                        ? 'bg-emerald-500 text-slate-950 hover:bg-emerald-400'
                        : 'border border-white/20 text-white hover:border-white'
                    }`}
                  >
                    {plan.cta.label}
                  </a>
                </GlassPanel>
              );
            })}
          </div>
        </MarketingSection>

        <MarketingSection theme="deep">
          <SectionHeader
            overline="Add-ons"
            title="Layer capabilities as your program matures"
            description="Each add-on plugs into the same policy, telemetry, and monetization fabric so governance stays consistent."
          />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {pricingAddOns.map((addon) => (
              <GlassPanel key={addon.name}>
                <p className="text-lg font-semibold text-white">{addon.name}</p>
                <p className="mt-3 text-sm text-slate-300">{addon.description}</p>
              </GlassPanel>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <div className="grid gap-12 lg:grid-cols-2">
            <div className="space-y-6">
              <SectionHeader
                overline="Usage-based pricing"
                title="Predictable overages without surprise invoices"
                description="Flexible overages keep you moving. Discounts apply automatically as invocations scale."
              />
              <div className="space-y-3">
                {pricingOverages.map((item) => (
                  <GlassPanel key={item.label} padding="sm" className="flex items-center justify-between">
                    <span className="text-sm text-slate-200">{item.label}</span>
                    <span className="text-sm font-semibold text-white">{item.value}</span>
                  </GlassPanel>
                ))}
              </div>
            </div>
            <GlassPanel padding="lg">
              <h3 className="text-lg font-semibold text-white">Volume discounts</h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-200">
                {pricingVolumeDiscounts.map((discount) => (
                  <li
                    key={discount.tier}
                    className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-5 py-3"
                  >
                    <span>{discount.tier}</span>
                    <span className="font-semibold text-emerald-300">{discount.discount}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-xs text-slate-400">
                Enterprise agreements include bespoke pricing, dedicated infrastructure, and data residency commitments.
              </p>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="deep">
          <SectionHeader
            overline="Support"
            title="Choose the partnership level that fits your team"
            description="Every managed customer receives documentation and community access; layer on faster SLAs and dedicated experts as you scale."
          />
          <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {pricingSupportPlans.map((tier) => (
              <GlassPanel key={tier.name} padding="lg">
                <div>
                  <h3 className="text-xl font-semibold text-white">{tier.name}</h3>
                  <p className="mt-1 text-sm text-slate-300">{tier.price}</p>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-200">
                  {tier.details.map((detail) => (
                    <li key={detail}>• {detail}</li>
                  ))}
                </ul>
              </GlassPanel>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <SectionHeader
            overline="Professional services"
            title="Accelerate adoption with expert help"
            description="From migrations to custom integrations, the Relay team can partner with you end-to-end."
          />
          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {pricingProfessionalServices.map((service) => (
              <GlassPanel key={service.name} padding="lg">
                <p className="text-sm uppercase tracking-wide text-emerald-300">{service.price}</p>
                <h3 className="mt-2 text-lg font-semibold text-white">{service.name}</h3>
                <p className="mt-3 text-sm text-slate-300">{service.description}</p>
              </GlassPanel>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection theme="deep">
          <SectionHeader
            overline="FAQ"
            title="Pricing FAQ"
            description="Answers to the questions teams ask as they scale Relay across regions and revenue programs."
            align="center"
          />
          <div className="mt-12 space-y-6">
            <GlassPanel padding="lg">
              <h3 className="text-lg font-semibold text-white">Can I self-host Relay and upgrade later?</h3>
              <p className="mt-2 text-sm text-slate-300">Yes. Start on the Community plan and migrate to managed Relay without changing your RUFIDs or manifests.</p>
            </GlassPanel>
            <GlassPanel padding="lg">
              <h3 className="text-lg font-semibold text-white">How are executions billed on Starter/Startup?</h3>
              <p className="mt-2 text-sm text-slate-300">Starter includes 500k executions and Startup includes 1M executions each month. Beyond the included limits, pay $0.50 per million invocations with automatic volume discounts as you scale.</p>
            </GlassPanel>
            <GlassPanel padding="lg">
              <h3 className="text-lg font-semibold text-white">What support is included for Enterprise?</h3>
              <p className="mt-2 text-sm text-slate-300">Enterprise customers receive 24/7 support, dedicated Slack, quarterly architecture reviews, and a success partner.</p>
            </GlassPanel>
            <GlassPanel padding="lg">
              <h3 className="text-lg font-semibold text-white">Do you offer discounts for startups or education?</h3>
              <p className="mt-2 text-sm text-slate-300">Yes—contact us with your program details and we’ll tailor a package.</p>
            </GlassPanel>
          </div>
        </MarketingSection>
      </div>
    </AppShell>
  );
};

export default PricingPage;
